import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Chip, LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useTheme, alpha } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { FerramentasDaListagemCadastros } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import { DialogOperacoes } from './DialogOperacoes';
import { EstoqueOperacoesService, IListagemOperacoes } from '../../../shared/services/api/estoque/estoqueOperacoes/EstoqueOperacoesService';
import { v4 as uuidv4 } from 'uuid';

export const ListagemDeOperacoes: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const guuid = uuidv4();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemOperacoes[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'operacoes';
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_LINHASSMALL);
  const [containerHeight, setContainerHeight] = useState<number>();
  const tabelaRef = useRef<HTMLDivElement>(null);
  const rowHeightRef = useRef<number>(0);
  const headerHeightRef = useRef<number>(0);
  const footerHeightRef = useRef<number>(0);

  const calcularAlturaContainer = () => {
    const container = tabelaRef.current;
    if (!container) return;

    // Mede o espaço real até o rodapé da janela em vez de usar um valor fixo de "calc(100vh - Npx)",
    // que ficava desalinhado com a altura real do título/toolbar e causava scroll na página inteira.
    // Só é recalculado em um resize real da janela (não a cada render), para a parte superior não
    // ficar se reposicionando sozinha e gerando atualizações em loop na listagem.
    const BOTTOM_GAP = 16;
    const novaAltura = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - BOTTOM_GAP);
    setContainerHeight(prev => (prev === novaAltura ? prev : novaAltura));
  };

  const calcularLimiteLinhas = () => {
    const container = tabelaRef.current;
    if (!container) return;

    const headerEl = container.querySelector('thead');
    const rowEl = rows.length > 0 ? container.querySelector('tbody tr') : null;
    const footerEl = container.querySelector('tfoot');

    if (headerEl) headerHeightRef.current = headerEl.getBoundingClientRect().height;
    if (rowEl) rowHeightRef.current = rowEl.getBoundingClientRect().height;
    if (footerEl) footerHeightRef.current = footerEl.getBoundingClientRect().height;

    if (!rowHeightRef.current || !containerHeight) return;

    const MIN_ROWS = 1;
    const MAX_ROWS = 100;
    const FOLGA_LINHAS = 1;
    // Usa o orçamento de altura calculado (containerHeight), não container.clientHeight:
    // quando a listagem tem poucos itens o container encolhe (height: 'auto'), e
    // clientHeight passaria a refletir só o conteúdo atual em vez do espaço disponível.
    const alturaDisponivel = containerHeight - headerHeightRef.current - footerHeightRef.current;
    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / rowHeightRef.current) - FOLGA_LINHAS)
    );

    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  useEffect(() => {
    calcularAlturaContainer();
    window.addEventListener('resize', calcularAlturaContainer);
    return () => window.removeEventListener('resize', calcularAlturaContainer);
  }, []);

  // Depende de totalCount (não de "rows") para não reagendar a cada refetch/página,
  // o que causava um looping de recálculo quando a altura medida variava por
  // arredondamento (ex: 1 item sem paginação, ou fetches acumulados ao voltar de outra aba).
  useEffect(() => {
    const id = setTimeout(calcularLimiteLinhas, 50);
    return () => clearTimeout(id);
  }, [totalCount, containerHeight]);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };
  const tipo = useMemo(() => searchParams.get('tipo') || 'descricao', [searchParams]);
  const filter = useMemo(() => searchParams.get('filter') || '', [searchParams]);
  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);

  const [open, setOpen] = useState(false);
  const [detalheOperacao, setDetalheOperacao] = useState<string>('');

  const handleClose = () => {
    setOpen(false);
  };
  useEffect(() => {
    setIsLoading(true);

    debounce(() => {
      EstoqueOperacoesService.getAll(pagina, filter, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        })
        .catch(() => {
          setIsLoading(false);
          setMsgType('error');
          setMsg('Erro ao carregar dados. Tente novamente mais tarde.');
          setOpenMsg(true);
        });
    });
  }, [pagina, filter, open, rowsPerPage, debounce]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, tipo, pagina: '1' }, { replace: true });
    }
  }, [rowsPerPage]);

  return (
    <LayoutBaseDePagina
      titulo='Listagem de Operações'
      barraDeFerramentas={
        <FerramentasDaListagemCadastros
          mostrarInputBusca
          mostrarInputBasicSelect
          textoDaBusca={filter}
          textoDoSelect={tipo}
          textoBotaoNovo='Nova Operação'
          aoClicarEmNovo={() => { setDetalheOperacao('novo'); setOpen(true); }}
          aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo, pagina: '1' }, { replace: true })}
          aoMudarTextoDoSelect={textoselect => setSearchParams({ filter, tipo: textoselect, pagina: '1' }, { replace: true })}
          dadosSelecao={[
            { nameBD: 'descricao', descricao: 'Descrição' },
            { nameBD: 'tipooperacao', descricao: 'Tipo de Operação' },
          ]}
        />
      }
    >
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openMsg}
        autoHideDuration={6000}
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={msgType}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,

        }}
      >
        <TableContainer
          ref={tabelaRef}
          sx={{
            // 'auto' quando a página atual não preenche rowsPerPage (única página curta ou
            // última página parcial de uma listagem paginada), para o fundo acompanhar a
            // quantidade real de itens em vez de esticar até o fim da tela.
            height: rows.length >= rowsPerPage
              ? (containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)')
              : 'auto',
            maxHeight: containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)',
            overflow: 'auto',
            scrollbarGutter: 'stable',
          }}
        >
          <Table size='small'>
            <TableHead>
              <TableRow sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                '& th': {
                  fontWeight: 'bold',
                  color: theme.palette.primary.dark,
                  fontSize: '0.95rem',
                  py: 2
                }
              }}>
                <TableCell>Descrição</TableCell>
                <TableCell>Tipo de Operação</TableCell>
                <TableCell>Tipo de Pagamento</TableCell>
                <TableCell align='center'>Faturamento</TableCell>
                <TableCell align='center'>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map(row => (
                <TableRow
                  key={row.idoperacoes}
                  hover
                  sx={{
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.03)
                    },
                    '&:last-child td': { borderBottom: 0 }
                  }}
                  onClick={() => { setDetalheOperacao(row.idoperacoes); setOpen(true); }}
                >
                  <TableCell>
                    <Typography variant='body1' fontWeight='medium' noWrap>
                      {row.descricao}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body1' noWrap>
                      {row.tipooperacao}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" noWrap>
                      {row.tpag === 1 ? 'À vista' : row.tpag === 2 ? 'A prazo' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={row.faturamento ? 'Sim' : 'Não'}
                      size='small'
                      color={row.faturamento ? 'success' : 'error'}
                      variant='outlined'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={row.status ? 'Ativo' : 'Inativo'}
                      size='small'
                      color={row.status ? 'success' : 'error'}
                      variant='outlined'
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {totalCount === 0 && !isLoading && (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={4} align='center' sx={{ py: 4 }}>
                    <Box>
                      <Typography variant='h6' color='text.secondary' gutterBottom>
                        {Environment.LISTAGEM_VAZIA}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {filter ? 'Tente ajustar os filtros de busca' : 'Clique em "Nova Operação" para começar'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0 }}>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {(totalCount > 0 && totalCount > rowsPerPage) && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ py: 2 }}>
                    <Box display='flex' justifyContent='center' alignItems='center' px={2}>
                      <Pagination
                        page={pagina}
                        count={Math.ceil(totalCount / rowsPerPage)}
                        onChange={(_, newPage) => setSearchParams(
                          { filter, tipo, pagina: newPage.toString() },
                          { replace: true }
                        )}
                        color='primary'
                        shape='rounded'
                        size='small'
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
      <DialogOperacoes
        id={detalheOperacao}
        value={guuid}
        open={open}
        onClose={handleClose}
      />
    </LayoutBaseDePagina>
  );
};